import { startOfDay, subYears } from "date-fns";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";

import { dateRangePresetOptions, MAX_DATE_RANGE_YEARS, MAX_HISTORICAL_YEARS } from "@/common/report.common";
import { MezzaDatePicker } from "@/components/core/Form/MezzaDatePicker";
import { MezzaSelectField } from "@/components/core/Form/MezzaSelectField";
import { DateRangePreset } from "@/types/report.types";
import { cn } from "@/lib/utils";

/** The date fields every report form has; see `useReportForm`. */
interface ReportDateFields extends FieldValues {
  dateRangePreset: DateRangePreset;
  startDate?: Date;
  endDate?: Date;
}

interface ReportDateRangeFieldsProps<T extends ReportDateFields> {
  readonly form: UseFormReturn<T>;
  readonly disabled?: boolean;
  readonly className?: string;
}

/**
 * Date range preset plus, for a custom range, start and end pickers limited to what the report API accepts
 * (no future dates, at most {@link MAX_HISTORICAL_YEARS} back and {@link MAX_DATE_RANGE_YEARS} long).
 */
export function ReportDateRangeFields<T extends ReportDateFields>({
  form,
  disabled,
  className,
}: ReportDateRangeFieldsProps<T>) {
  // The generic form cannot be narrowed to these field names, so read and write them through a typed view.
  const dateForm = form as unknown as UseFormReturn<ReportDateFields>;
  const isCustomDateRange = dateForm.watch("dateRangePreset") === DateRangePreset.CUSTOM;
  const startDate = dateForm.getValues("startDate");

  const shouldDisableStartDate = (date: Date): boolean =>
    date > new Date() || date < subYears(new Date(), MAX_HISTORICAL_YEARS);

  const shouldDisableEndDate = (date: Date): boolean => {
    if (!startDate) return true;
    return date > new Date() || date < startDate || date > subYears(startDate, -MAX_DATE_RANGE_YEARS);
  };

  const handleDateRangePresetChange = (value: string) => {
    if (value !== DateRangePreset.CUSTOM) {
      dateForm.setValue("startDate", undefined, { shouldValidate: true });
      dateForm.setValue("endDate", undefined, { shouldValidate: true });
    }
  };

  return (
    <>
      <MezzaSelectField
        form={form}
        name={"dateRangePreset" as Path<T>}
        label="generateReport.dateRangePreset"
        placeholder="generateReport.dateRangePresetPlaceholder"
        options={dateRangePresetOptions}
        disabled={disabled}
        onValueChange={handleDateRangePresetChange}
        fullWidthOptions
      />

      {isCustomDateRange && (
        <div className={cn("grid grid-cols-1 gap-4 sm:grid-cols-2", className)}>
          <MezzaDatePicker
            form={form}
            name={"startDate" as Path<T>}
            label="generateReport.startDate"
            placeholder="generateReport.pickDatePlaceholder"
            disabled={disabled}
            shouldDisableDate={shouldDisableStartDate}
            calendarProps={{
              captionLayout: "dropdown",
              fromYear: startOfDay(subYears(new Date(), MAX_HISTORICAL_YEARS)).getFullYear(),
              toYear: new Date().getFullYear(),
            }}
          />

          <MezzaDatePicker
            form={form}
            name={"endDate" as Path<T>}
            label="generateReport.endDate"
            placeholder="generateReport.pickDatePlaceholder"
            disabled={disabled}
            shouldDisableDate={shouldDisableEndDate}
            calendarProps={{
              captionLayout: "dropdown",
              fromYear: startOfDay(subYears(startDate || new Date(), MAX_DATE_RANGE_YEARS)).getFullYear(),
              toYear: new Date().getFullYear(),
            }}
          />
        </div>
      )}
    </>
  );
}
